<template>
    <div>
        <!-- Backdrop -->
        <div
            v-if="isOpen"
            class="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
            @click="closePanel"
        ></div>

        <!-- Slide-out Panel -->
        <div
            class="fixed top-0 right-0 h-screen w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col"
            :class="isOpen ? 'translate-x-0' : 'translate-x-full'"
        >
        <div class="p-6 flex flex-col flex-1 min-h-0 overflow-hidden">
            <!-- Header -->
            <div class="flex items-center justify-between mb-6 pb-4 border-b">
                <h2 class="text-xl font-semibold text-gray-900 flex items-center gap-2">
                    <span>{{ contact_id ? (contact_name || $t('Contact Details')) : $t('Add New Contact') }}</span>
                    <i v-if="form.preferred_vendor" class="fas fa-star text-yellow-500 text-sm mb-1" title="Preferred Vendor"></i>
                </h2>
                <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600 text-2xl focus:outline-none focus:ring-0"
                    @click="closePanel"
                >
                    <i class="far fa-times-circle"></i>
                </button>
            </div>

            <!-- Contact Info Section -->
            <div class="mb-6" :class="isEditing ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'flex-shrink-0'">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-md font-medium text-gray-400">{{ $t('Contact Information') }}</h3>

                    <button
                        v-if="!isEditing"
                        type="button"
                        @click="isEditing = true"
                        class="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <i class="fas fa-edit mr-1"></i>
                        {{ $t('Edit') }}
                    </button>
                </div>

                <!-- View Mode -->
                <div v-if="!isEditing" class="space-y-3">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Name') }}</label>
                            <div class="mt-1 text-md text-black">{{ form.name || '—' }}</div>
                        </div>
                        <div>
                            <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Company') }}</label>
                            <div class="mt-1 text-md text-black">{{ form.company || '—' }}</div>
                        </div>
                        <div>
                            <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Email') }}</label>
                            <div class="mt-1 text-md text-black">
                                <a v-if="form.email" :href="'mailto:' + form.email" class="text-blue-600 hover:text-blue-800">
                                    {{ form.email }}
                                </a>
                                <span v-else>—</span>
                            </div>
                        </div>
                        <div>
                            <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Phone') }}</label>
                            <div class="mt-1 text-md text-black">
                                {{ form.phone || '—' }}
                            </div>
                        </div>
                    </div>

                    <div v-if="form.address_1 || form.city" class="mt-4">
                        <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Address') }}</label>
                        <div class="mt-1 text-sm text-gray-900">
                            {{ formatAddress(form) || '—' }}
                        </div>
                    </div>

                    <div v-if="form.types && form.types.length > 0" class="mt-4">
                        <label class="text-2xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Contact Types') }}</label>
                        <div class="mt-2 flex flex-wrap gap-2">
                            <span
                                v-for="type in form.types"
                                :key="type"
                                class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                                {{ formatType(type) }}
                            </span>
                        </div>
                    </div>

                    <div v-if="form.preferred_vendor" class="mt-4">
                        <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            <i class="fas fa-star mr-1"></i>
                            {{ $t('Preferred Vendor') }}
                        </span>
                    </div>
                </div>

                <!-- Edit Mode -->
                <form v-else @submit.prevent="saveContact" class="flex-1 min-h-0 flex flex-col overflow-hidden">
                    <div class="space-y-4 overflow-y-auto flex-1">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input-text
                            name="name"
                            v-model="form.name"
                            label="Name"
                            placeholder="Name"
                            :required="true"
                            :error="errors.name ? errors.name[0] : ''"
                        />
                        <input-text
                            name="company"
                            v-model="form.company"
                            label="Company"
                            placeholder="Company"
                            :error="errors.company ? errors.company[0] : ''"
                        />
                        <input-text
                            name="email"
                            v-model="form.email"
                            label="Email"
                            placeholder="Email"
                            type="email"
                            :error="errors.email ? errors.email[0] : ''"
                        />
                        <input-text
                            name="phone"
                            v-model="form.phone"
                            label="Phone"
                            placeholder="Phone"
                            :error="errors.phone ? errors.phone[0] : ''"
                        />
                    </div>

                    <input-text
                        name="address_1"
                        v-model="form.address_1"
                        label="Address"
                        placeholder="Address"
                        :error="errors.address_1 ? errors.address_1[0] : ''"
                    />

                    <input-text
                        name="address_2"
                        v-model="form.address_2"
                        label="Address 2"
                        placeholder="Address 2"
                        :error="errors.address_2 ? errors.address_2[0] : ''"
                    />

                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <input-text
                            name="city"
                            v-model="form.city"
                            label="City"
                            placeholder="City"
                            :error="errors.city ? errors.city[0] : ''"
                        />
                        <input-select-states
                            name="state"
                            v-model="form.state"
                            label="State"
                            no_selection="Select State"
                        />
                        <input-text
                            name="zip"
                            v-model="form.zip"
                            label="Zip"
                            placeholder="Zip"
                            :error="errors.zip ? errors.zip[0] : ''"
                        />
                    </div>

                    <div>
                        <input-label>Contact Types</input-label>
                        <div class="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div v-for="columnNum in [1, 2, 3]" :key="columnNum" class="space-y-2">
                                <label
                                    v-for="typeOption in typesByColumn[columnNum]"
                                    :key="typeOption.value"
                                    class="flex items-center space-x-2"
                                    :class="assignedTypes.has(typeOption.value) ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'"
                                >
                                    <input
                                        type="checkbox"
                                        :value="typeOption.value"
                                        v-model="form.types"
                                        :disabled="assignedTypes.has(typeOption.value)"
                                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span class="text-sm text-gray-700">
                                        {{ $t(typeOption.label) }}
                                        <span v-if="assignedTypes.has(typeOption.value)" class="text-xs text-gray-500 ml-1" title="This type is assigned to properties and cannot be removed">
                                            (assigned)
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                        <p v-if="errors.types" class="text-red-600 text-sm mt-1">{{ errors.types[0] }}</p>
                        <p v-if="assignedTypes.size > 0" class="text-xs text-gray-500 mt-2">
                            <i class="fas fa-info-circle mr-1"></i>
                            Types marked as "assigned" are currently used on properties and cannot be removed.
                        </p>
                    </div>

                    <div class="border-t pt-4 mt-4">
                        <input-label>{{ $t('Preferred Vendor?') }}</input-label>
                        <div class="mt-2">
                            <label class="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    v-model="form.preferred_vendor"
                                    class="sr-only peer"
                                />
                                <div class="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    </div>

                    <div v-if="error" class="text-red-600 font-semibold text-sm">
                        {{ error }}
                    </div>
                    </div>

                    <div class="flex items-center justify-between pt-4 border-t flex-shrink-0 mt-auto">
                        <button
                            v-if="contact_id"
                            type="button"
                            class="flex items-center text-sm text-red-600 hover:text-red-700 leading-none"
                            @click="deleteContact"
                        >
                            <i class="far fa-trash-alt mr-2 mb-0.5"></i>
                            {{ $t('Delete Contact') }}
                        </button>

                        <div class="flex items-center space-x-4 ml-auto">
                            <button-gray-outline @click="cancelEdit">
                                {{ $t('Cancel') }}
                            </button-gray-outline>

                            <button-blue type="submit" :disabled="is_submitting">
                                {{ is_submitting ? $t('Saving...') : $t('Save') }}
                            </button-blue>
                        </div>
                    </div>
                </form>
            </div>

            <!-- Properties Section -->
            <div v-if="(contact_id || (groupedProperties && groupedProperties.length > 0)) && !isEditing" class="border-t pt-6 flex-1 min-h-0 flex flex-col">
                <h3 class="text-md font-medium text-gray-400 mb-4 flex-shrink-0">
                    {{ $t('Properties') }}
                </h3>

                <div v-if="groupedProperties && groupedProperties.length > 0" class="border border-gray-200 rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
                    <div class="overflow-y-auto flex-1">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-3 pt-4 pb-3 text-left text-2xs leading-none font-medium text-gray-500 uppercase tracking-wider">
                                    {{ $t('Property') }}
                                </th>
                                <th class="px-3 pt-4 pb-3 text-left text-2xs leading-none font-medium text-gray-500 uppercase tracking-wider">
                                    {{ $t('MLS #') }}
                                </th>
                                <th class="px-3 pt-4 pb-3 text-left text-2xs leading-none font-medium text-gray-500 uppercase tracking-wider">
                                    {{ $t('Roles') }}
                                </th>
                                <th class="px-3 pt-4 pb-3 text-right text-2xs leading-none font-medium text-gray-500 uppercase tracking-wider">
                                    {{ $t('Actions') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr
                                v-for="property in groupedProperties"
                                :key="property.id"
                                class="hover:bg-gray-50"
                            >
                                <td class="px-3 py-3 whitespace-nowrap">
                                    <div class="text-sm text-gray-900">
                                        {{ formatAddress(property.property) }}
                                    </div>
                                </td>
                                <td class="px-3 py-3 whitespace-nowrap">
                                    <div class="text-sm text-gray-500">
                                        {{ property.mls_number || '—' }}
                                    </div>
                                </td>
                                <td class="px-3 py-3">
                                    <div class="flex flex-wrap gap-1">
                                        <span
                                            v-for="role in property.roles"
                                            :key="role"
                                            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                        >
                                            {{ formatType(role) }}
                                        </span>
                                    </div>
                                </td>
                                <td class="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                                    <a
                                        :href="'/properties/' + property.id"
                                        class="text-blue-600 hover:text-blue-900"
                                    >
                                        {{ $t('View') }}
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    </div>
                </div>

                <div v-else class="text-center text-gray-500 py-8 flex-shrink-0">
                    {{ $t('No properties found.') }}
                </div>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import InputText from '../../components/input-text.vue';
import InputLabel from '../../components/input-label.vue';
import InputSelectStates from '../../components/input-select-states.vue';
import ButtonBlue from '../../components/button-blue.vue';
import ButtonGrayOutline from '../../components/button-gray-outline.vue';

export default {
    components: {
        InputText,
        InputLabel,
        InputSelectStates,
        ButtonBlue,
        ButtonGrayOutline,
    },

    props: {
        isOpen: { type: Boolean, default: false },
        contact_id: { default: null },
        contact_name: { default: '' },
        contact_data: { default: () => ({}) },
        properties: { default: () => [] },
    },

    data() {
        return {
            isEditing: false,
            is_submitting: false,
            error: null,
            errors: {},
            form: {
                contact_id: null,
                name: '',
                company: '',
                email: '',
                phone: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                zip: '',
                types: [],
                preferred_vendor: false,
            },
            available_types: [
                { label: 'Buyer Lender', value: 'buyer_lender', column: 1},
                { label: 'Buyer Broker', value: 'buyer_broker', column: 1},
                { label: 'Buyer Mortgage Broker', value: 'buyer_mortgage_broker', column: 1},
                { label: 'Buyer Real Estate Agent', value: 'buyer_real_estate_agent', column: 1},
                { label: 'Buyer Attorney', value: 'buyer_attorney', column: 1},
                { label: 'Closing Agent', value: 'closing_agent', column: 1},
                { label: 'Inspector', value: 'inspector', column: 1},
                { label: 'Insurance Agent', value: 'insurance_agent', column: 1},
                { label: 'Future Buyer', value: 'future_buyer', column: 1},

                { label: 'Seller Broker', value: 'seller_broker', column: 2 },
                { label: 'Seller Attorney', value: 'seller_attorney', column: 2 },
                { label: 'Future Seller', value: 'future_seller', column: 2 },

                { label: 'Home Office Coordinator', value: 'home_office_coordinator', column: 3 },
                { label: 'Transaction Coordinator', value: 'transaction_coordinator', column: 3 },
                { label: 'Managing Broker', value: 'managing_broker', column: 3 },
                { label: 'Office Manager', value: 'office_manager', column: 3 },
            ],
        };
    },

    computed: {
        typesByColumn() {
            const columns = { 1: [], 2: [], 3: [] };
            this.available_types.forEach(type => {
                const column = type.column || 1;
                if (column >= 1 && column <= 3) {
                    columns[column].push(type);
                }
            });
            return columns;
        },

        groupedProperties() {
            if (!this.properties || this.properties.length === 0) {
                return [];
            }

            // Group properties by property ID
            const grouped = {};
            this.properties.forEach(prop => {
                if (!prop.id) return;

                if (!grouped[prop.id]) {
                    grouped[prop.id] = {
                        id: prop.id,
                        mls_number: prop.mls_number,
                        property: prop.property,
                        roles: [],
                    };
                }

                // Add role if not already present
                if (prop.type && !grouped[prop.id].roles.includes(prop.type)) {
                    grouped[prop.id].roles.push(prop.type);
                }
            });

            // Sort roles for consistent display
            Object.values(grouped).forEach(prop => {
                prop.roles.sort();
            });

            return Object.values(grouped);
        },

        assignedTypes() {
            // Get all types that are currently assigned to properties
            // These should not be removable
            const assigned = new Set();
            if (this.properties && this.properties.length > 0) {
                this.properties.forEach(prop => {
                    if (prop.type) {
                        assigned.add(prop.type);
                    }
                });
            }
            return assigned;
        },
    },

    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.loadContactData();
                // Prevent body scroll when panel is open
                document.body.style.overflow = 'hidden';
            } else {
                this.resetForm();
                // Restore body scroll when panel is closed
                document.body.style.overflow = '';
            }
        },
        contact_data: {
            deep: true,
            handler() {
                if (this.isOpen) {
                    this.loadContactData();
                }
            },
        },
    },

    methods: {
        loadContactData() {
            if (this.contact_data && Object.keys(this.contact_data).length > 0) {
                // Handle types - they might be objects with .type property or strings
                let types = [];
                if (Array.isArray(this.contact_data.types)) {
                    types = this.contact_data.types.map(t => {
                        return typeof t === 'string' ? t : (t.type || t);
                    });
                }

                this.form = {
                    contact_id: this.contact_data.contact_id || this.contact_id,
                    name: this.contact_data.name || '',
                    company: this.contact_data.company || '',
                    email: this.contact_data.email || '',
                    phone: this.contact_data.phone || '',
                    address_1: this.contact_data.address_1 || '',
                    address_2: this.contact_data.address_2 || '',
                    city: this.contact_data.city || '',
                    state: this.contact_data.state || '',
                    zip: this.contact_data.zip || '',
                    types: types,
                    preferred_vendor: this.contact_data.preferred_vendor || false,
                };
            } else {
                this.resetForm();
            }
            // Determine if this is a new contact or existing contact
            // It's a new contact only if:
            // 1. No contact_id prop AND
            // 2. No contact_data OR contact_data is empty OR contact_data has no identifying info (name/email)
            const hasContactId = this.contact_id || (this.contact_data && this.contact_data.contact_id);
            const hasContactData = this.contact_data && Object.keys(this.contact_data).length > 0;
            const hasIdentifyingInfo = hasContactData && (this.contact_data.name || this.contact_data.email);
            
            // Only start in edit mode if it's truly a new contact (no ID and no existing data)
            this.isEditing = !hasContactId && !hasIdentifyingInfo;
            this.error = null;
            this.errors = {};
        },

        resetForm() {
            this.form = {
                contact_id: null,
                name: '',
                company: '',
                email: '',
                phone: '',
                address_1: '',
                address_2: '',
                city: '',
                state: '',
                zip: '',
                types: [],
                preferred_vendor: false,
            };
            this.isEditing = false;
            this.is_submitting = false;
            this.error = null;
            this.errors = {};
        },

        closePanel() {
            this.$emit('close');
        },

        cancelEdit() {
            if (this.contact_id) {
                // If editing existing contact, reset to view mode
                this.loadContactData();
            } else {
                // If adding new contact, close the panel
                this.closePanel();
            }
        },

        async saveContact() {
            this.is_submitting = true;
            this.error = null;
            this.errors = {};

            try {
                let response;
                if (this.form.contact_id) {
                    response = await axios.patch(`/broker/contacts/${this.form.contact_id}`, this.form);
                } else {
                    response = await axios.post('/broker/contacts', this.form);
                }
                this.$emit('saved', response.data.contact);
                this.isEditing = false;
                // Reload to refresh the list
                window.location.reload();
            } catch (error) {
                if (error.response && error.response.data.errors) {
                    this.errors = error.response.data.errors;
                } else {
                    this.error = error.response.data.message || 'An unexpected error occurred.';
                }
            } finally {
                this.is_submitting = false;
            }
        },

        async deleteContact() {
            if (!confirm(this.$t('Are you sure you want to delete this contact?'))) {
                return;
            }

            this.is_submitting = true;
            this.error = null;

            try {
                await axios.delete(`/broker/contacts/${this.form.contact_id}`);
                this.$emit('deleted', this.form.contact_id);
                // Reload to refresh the list
                window.location.reload();
            } catch (error) {
                this.error = error.response.data.message || 'An unexpected error occurred.';
            } finally {
                this.is_submitting = false;
            }
        },

        formatAddress(object) {
            if (!object) return '';
            
            return [
                object.address || object.address_1,
                object.address_2,
                object.city,
                [object.state, object.zip].join(' '),
            ].filter(value => value && value.trim())
            .join(', ') || 'No address';
        },

        formatType(type) {
            const typeMap = {
                'buyer_1': 'Buyer',
                'buyer_2': 'Buyer',
                'seller_1': 'Seller',
                'seller_2': 'Seller',
                'closing_agent': 'Closing Agent',
                'home_office_coordinator': 'Home Office Coordinator',
                'managing_broker': 'Managing Broker',
                'office_manager': 'Office Manager',
                'transaction_coordinator': 'Transaction Coordinator',
                'seller_broker': 'Seller Broker',
                'buyer_broker': 'Buyer Broker',
                'seller_attorney': 'Seller Attorney',
                'buyer_lender': 'Buyer Lender',
                'inspector': 'Inspector',
                'buyer_mortgage_broker': 'Buyer Mortgage Broker',
                'buyer_attorney': 'Buyer Attorney',
                'buyer_real_estate_agent': 'Buyer Real Estate Agent',
                'broker_contact': 'Broker Contact',
                'buyer_contact': 'Buyer Contact',
                'insurance_agent': 'Insurance Agent',
            };
            
            return typeMap[type] || this.titleCase(type);
        },

        titleCase(str) {
            return str.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
                .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());
        },

        handleEscKey(event) {
            if (event.key === 'Escape' && this.isOpen) {
                this.closePanel();
            }
        },
    },

    mounted() {
        document.addEventListener('keydown', this.handleEscKey);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleEscKey);
        // Ensure body scroll is restored when component is destroyed
        document.body.style.overflow = '';
    },
}
</script>

<style scoped>
/* Ensure panel stays on top */
.z-50 {
    z-index: 50;
}

.z-40 {
    z-index: 40;
}
</style>

